import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useTheme } from '../context/ThemeContext';

const PriceTable = ({ title, data, icon: Icon, color = '#d4af37' }) => {
  const { selectedCurrency, currencyConfig } = useCurrency();
  const { theme } = useTheme();
  const config = currencyConfig[selectedCurrency];

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    
    if (price >= 10000) {
      return price.toLocaleString(config.locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }
    if (price >= 100) {
      return price.toLocaleString(config.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (price < 1) {
      return price.toFixed(4);
    }
    return price.toFixed(2);
  };

  const getChange = (current, previous) => {
    if (!previous) return { diff: 0, percent: '0.00' };
    const diff = current - previous;
    const percent = ((diff / previous) * 100).toFixed(2);
    return { diff, percent };
  };

  const getChangeClass = (current, previous) => {
    if (current > previous) return 'text-green-400';
    if (current < previous) return 'text-red-400';
    return '';
  };

  const getChangeBg = (current, previous) => {
    if (current > previous) return 'bg-green-500/10';
    if (current < previous) return 'bg-red-500/10';
    return '';
  };

  const getChangeIcon = (current, previous) => {
    if (current > previous) return <TrendingUp className="w-3 h-3" />;
    if (current < previous) return <TrendingDown className="w-3 h-3" />;
    return <Minus className="w-3 h-3" />;
  };

  return (
    <div 
      className="rounded-xl border overflow-hidden shadow-xl transition-colors duration-300"
      style={{ backgroundColor: theme.bgCard, borderColor: theme.border }}
    >
      {/* Header */}
      <div 
        className="px-4 py-3 border-b flex items-center justify-between"
        style={{ backgroundColor: theme.bgTertiary, borderColor: theme.border }}
      >
        <div className="flex items-center gap-3">
          {Icon && (
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${color}20` }}
            >
              <Icon className="w-4 h-4" style={{ color }} />
            </div>
          )}
          <h2 className="font-semibold text-sm uppercase tracking-wider" style={{ color: theme.text }}>{title}</h2>
        </div>
        <span 
          className="text-xs px-2 py-1 rounded"
          style={{ backgroundColor: theme.bgHover, color: theme.textMuted }}
        >
          {config.symbol} {selectedCurrency}
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-xs uppercase border-b" style={{ color: theme.textMuted, borderColor: theme.border }}>
              <th className="px-4 py-3 text-left font-medium">Product</th>
              <th className="px-4 py-3 text-right font-medium">Bid</th>
              <th className="px-4 py-3 text-right font-medium">Ask</th>
              <th className="px-4 py-3 text-right font-medium">Change</th>
              <th className="px-4 py-3 text-right font-medium">%</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => {
              const askChange = getChange(item.ask, item.prevAsk);
              
              return (
                <tr 
                  key={item.id}
                  className={`border-b transition-colors duration-150`}
                  style={{ borderColor: `${theme.border}50` }}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span 
                        className="text-xs font-bold px-2 py-1 rounded"
                        style={{ backgroundColor: `${color}20`, color }}
                      >
                        {item.alias}
                      </span>
                      <span className="font-medium text-sm" style={{ color: theme.text }}>{item.name}</span>
                    </div>
                  </td>
                  <td className={`px-4 py-3 text-right font-mono text-sm ${getChangeClass(item.bid, item.prevBid)} ${getChangeBg(item.bid, item.prevBid)} rounded-l`}>
                    {config.symbol}{formatPrice(item.bid)}
                  </td>
                  <td className={`px-4 py-3 text-right font-mono text-sm ${getChangeClass(item.ask, item.prevAsk)} ${getChangeBg(item.ask, item.prevAsk)}`}>
                    {config.symbol}{formatPrice(item.ask)}
                  </td>
                  <td className={`px-4 py-3 text-right font-mono text-xs ${getChangeClass(item.ask, item.prevAsk)}`} style={!getChangeClass(item.ask, item.prevAsk) ? { color: theme.textSecondary } : {}}>
                    <span className="flex items-center justify-end gap-1">
                      {getChangeIcon(item.ask, item.prevAsk)}
                      {askChange.diff > 0 ? '+' : ''}{askChange.diff.toFixed(2)}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-right font-mono text-xs ${getChangeClass(item.ask, item.prevAsk)} rounded-r`}>
                    <span className={`px-2 py-1 rounded text-xs ${getChangeBg(item.ask, item.prevAsk)}`}>
                      {askChange.percent > 0 ? '+' : ''}{askChange.percent}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PriceTable;
